import { useEffect, useState } from 'react';
import { WinnerAnnouncement } from '../../../hooks/useWinnerBroadcast';

const FONT_HUD  = "'Orbitron', monospace";
const FONT_DECO = "'Cinzel', serif";
const FONT_BODY = "'Rajdhani', 'Noto Sans TC', system-ui, sans-serif";

interface Props {
  announcement: WinnerAnnouncement;
  onDismiss: () => void;
  isJudge?: boolean;
}

function Star({ delay, color }: { delay: number; color: string }) {
  return (
    <i className="ri-star-fill absolute text-xl animate-ping"
      style={{
        color,
        opacity: 0.6,
        animationDelay: `${delay}ms`,
        animationDuration: '1.8s',
        top: `${15 + Math.random() * 70}%`,
        left: `${5 + Math.random() * 90}%`,
      }} />
  );
}

export default function WinnerAnnouncementOverlay({ announcement, onDismiss, isJudge }: Props) {
  const [phase, setPhase] = useState<'enter' | 'show' | 'exit'>('enter');
  const [stars] = useState(() =>
    Array.from({ length: 12 }, (_, i) => ({
      id: i,
      delay: i * 150,
    }))
  );

  const isBlack  = announcement.winnerTeam === 'black';
  const color    = isBlack ? '#d4a843' : '#e8e8e8';
  const rgb      = isBlack ? '212,168,67' : '232,232,232';
  const teamName = isBlack ? '黑隊' : '白隊';
  const modeLabel = announcement.mode === 'team' ? '隊伍' : '個人';

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('show'), 50);
    return () => clearTimeout(t1);
  }, []);

  const handleDismiss = () => {
    setPhase('exit');
    setTimeout(onDismiss, 400);
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{
        background: 'rgba(0,0,0,0.92)',
        opacity: phase === 'enter' ? 0 : phase === 'exit' ? 0 : 1,
        transition: 'opacity 0.4s ease',
      }}
      onClick={handleDismiss}
    >
      {/* Ambient glow bg */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full"
          style={{
            background: `radial-gradient(circle, rgba(${rgb},0.12) 0%, transparent 70%)`,
            animation: 'winnerPulse 2.5s ease-in-out infinite',
          }} />
        {stars.map(s => (
          <Star key={s.id} delay={s.delay} color={color} />
        ))}
      </div>

      {/* Main card */}
      <div
        className="relative w-[88vw] max-w-sm mx-auto rounded-3xl overflow-hidden"
        style={{
          background: `linear-gradient(160deg, rgba(${rgb},0.12) 0%, #07070f 50%, #0a0810 100%)`,
          border: `1.5px solid rgba(${rgb},0.4)`,
          boxShadow: `0 0 60px rgba(${rgb},0.2), 0 0 120px rgba(${rgb},0.08)`,
          transform: phase === 'show' ? 'scale(1) translateY(0)' : 'scale(0.85) translateY(30px)',
          opacity: phase === 'show' ? 1 : 0,
          transition: 'transform 0.5s cubic-bezier(0.175,0.885,0.32,1.275), opacity 0.4s ease',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Top shimmer line */}
        <div className="absolute top-0 left-0 right-0 h-px"
          style={{ background: `linear-gradient(90deg, transparent, rgba(${rgb},0.8), transparent)` }} />

        {/* Header */}
        <div className="px-6 pt-7 pb-4 text-center relative">
          {/* Crown / trophy */}
          <div className="relative inline-flex items-center justify-center mb-4">
            <div className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{
                background: `radial-gradient(circle, rgba(${rgb},0.2), rgba(${rgb},0.04))`,
                border: `2px solid rgba(${rgb},0.5)`,
                boxShadow: `0 0 30px rgba(${rgb},0.3)`,
              }}>
              <i className="ri-trophy-fill text-4xl"
                style={{ color, filter: `drop-shadow(0 0 12px rgba(${rgb},0.8))` }} />
            </div>
            {/* Orbit ring */}
            <div className="absolute inset-0 rounded-full"
              style={{
                border: `1px solid rgba(${rgb},0.2)`,
                animation: 'winnerOrbit 4s linear infinite',
              }} />
          </div>

          {/* Mode label */}
          <p className="text-[9px] font-black tracking-[0.5em] uppercase mb-2"
            style={{ color: `rgba(${rgb},0.5)`, fontFamily: FONT_DECO }}>
            {modeLabel}評分 · 總結算
          </p>

          {/* Title */}
          <h2 className="text-2xl font-black mb-1"
            style={{ color: 'white', fontFamily: FONT_DECO, textShadow: `0 0 20px rgba(${rgb},0.4)` }}>
            得獎公布
          </h2>
        </div>

        {/* Divider */}
        <div className="mx-6 h-px" style={{ background: `linear-gradient(90deg, transparent, rgba(${rgb},0.3), transparent)` }} />

        {/* Winner info */}
        <div className="px-6 py-6 text-center">
          {/* Team badge */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-4"
            style={{
              background: `rgba(${rgb},0.1)`,
              border: `1px solid rgba(${rgb},0.3)`,
            }}>
            <i className={`${isBlack ? 'ri-shield-fill' : 'ri-sword-fill'} text-xs`} style={{ color }} />
            <span className="text-[10px] font-black" style={{ color, fontFamily: FONT_BODY }}>{teamName}</span>
          </div>

          {/* Winner name */}
          <p className="text-4xl font-black mb-2 leading-tight"
            style={{ color, fontFamily: FONT_HUD, textShadow: `0 0 20px rgba(${rgb},0.6)`, letterSpacing: '0.02em' }}>
            {announcement.winnerName}
          </p>

          {/* Score */}
          <div className="flex items-center justify-center gap-3 mt-4">
            <div className="text-center">
              <p className="text-3xl font-black tabular-nums leading-none"
                style={{ color, fontFamily: FONT_HUD, textShadow: `0 0 12px rgba(${rgb},0.5)` }}>
                {announcement.totalScore.toFixed(1)}
              </p>
              <p className="text-[9px] mt-1" style={{ color: `rgba(${rgb},0.4)`, fontFamily: FONT_BODY }}>
                累積總分
              </p>
            </div>
          </div>

          {/* Stars */}
          <div className="flex items-center justify-center gap-1 mt-4">
            {[1, 2, 3, 4, 5].map(s => (
              <i key={s} className="ri-star-fill text-lg"
                style={{ color, filter: `drop-shadow(0 0 6px rgba(${rgb},0.6))`, animationDelay: `${s * 100}ms` }} />
            ))}
          </div>
        </div>

        {/* Dismiss button */}
        <div className="px-6 pb-6">
          <button
            onClick={handleDismiss}
            className="w-full py-3 rounded-2xl text-xs font-black cursor-pointer transition-all whitespace-nowrap"
            style={{
              background: `linear-gradient(135deg, rgba(${rgb},0.18), rgba(${rgb},0.08))`,
              color,
              border: `1px solid rgba(${rgb},0.35)`,
              fontFamily: FONT_BODY,
            }}>
            {isJudge ? '關閉廣播' : '知道了！'}
          </button>
        </div>

        {/* Bottom shimmer line */}
        <div className="absolute bottom-0 left-0 right-0 h-px"
          style={{ background: `linear-gradient(90deg, transparent, rgba(${rgb},0.4), transparent)` }} />
      </div>

      <style>{`
        @keyframes winnerPulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          50%       { transform: translate(-50%, -50%) scale(1.15); opacity: 0.7; }
        }
        @keyframes winnerOrbit {
          from { transform: rotate(0deg) scale(1.3); }
          to   { transform: rotate(360deg) scale(1.3); }
        }
      `}</style>
    </div>
  );
}
